import {
  LANGUAGE_REGEX,
  ROUTER_BASE_URL,
  ZITADEL,
} from '@/ts/utils/consts';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {pagesPath} from '@/ts/router/pages-path';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLang} from '@/ts/composables/stateful/use-lang';
import {useUserStore} from '@/ts/store/user-store';
import {useRouter, type Router} from 'vue-router';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {analyticsService} from '@/ts/services/analytics-service';
import type {Api} from '@/ts/api';


//
// Generates a secure random string of the specified length.
// Same as Math.random for hex string output but secure
//
function generateSecureRandomString(byteLength: number): string {
  // Create an array with the specified number of bytes
  const array = new Uint8Array(byteLength);

  // Populate the array with secure random values
  window.crypto.getRandomValues(array);

  // Convert the byte array to a string of hexadecimal characters
  // 16 0-9a-f characters per byte
  // 2 is 2 symb max
  return Array.from(array, byte => (`0${Number(byte).toString(16)}`).slice(-2)).join('');
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  // Step 1: Convert ArrayBuffer to Uint8Array
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  // Step 2: Create binary string from Uint8Array
  for (let index = 0; index < len; index++) {
    binary += String.fromCharCode(bytes[index]);
  }

  // Step 3: Encode binary string to Base64
  return window.btoa(binary);
}


//
// Creates a PKCE (Proof Key for Code Exchange):
//
async function getCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  const base64Digest = arrayBufferToBase64(digest);
  // Make it urls safe for query params
  return String(base64Digest)
    .replace(/\+/gu, '-')
    .replace(/\//gu, '_')
    .replace(/[=]/gu, '');
}

/**
 * Whether navigating to `path` would land on the page already open. Every route carries the
 * language prefix and no caller passes one, so the comparison drops it from the router's side.
 */
function isCurrentPage(router: Router, path: string): boolean {
  return (router.currentRoute.value.path.replace(LANGUAGE_REGEX.REMOVE_LANG_PREFIX, '') || '/') === path;
}

/** Returns the app base URL, accounting for ROUTER_BASE_URL in PR preview deployments */
export function getAppBaseUrl(): string {
  if (!ROUTER_BASE_URL) {
    return window.location.origin;
  }
  return `${window.location.origin}${String(ROUTER_BASE_URL).slice(0, -1)}#`;
}

/** Builds the OAuth redirect URI based on the current origin and router base URL */
export function getRedirectUri(): string {
  return `${getAppBaseUrl()}${pagesPath.public.continueAuth}`;
}

/** Returns a function that initiates the PKCE-based OIDC sign-in flow via Zitadel */
export function useSignIn() {
  const api = useApi();
  const {lang} = useLang();
  // loginHint prefills the email field on Zitadel's hosted login (e.g. right
  // after sign-up or email verification, where the email is already known).
  // `directToGoogle` skips Zitadel's sign-in screen and lands on Google's account chooser, for the
  // "Continue with Google" button. Its one caller is behind the same `google_idp_id` the scope
  // comes from, so the button and the direct destination appear and disappear together.
  return async function signIn(
    loginHint?: string | null,
    options?: {directToGoogle?: boolean},
  ): Promise<void> {
    // zitadel uses 108 characetsr for code verifier
    const codeVerifier = generateSecureRandomString(102);
    const codeChallenge = await getCodeChallenge(codeVerifier);
    persistentStorage.codeVerifier = codeVerifier;
    api.zitadel.redirectToSignIn(getRedirectUri(), codeChallenge, ZITADEL, {
      lang: lang.value,
      loginHint,
      idpId: options?.directToGoogle ? ZITADEL.google_idp_id : null,
    });
  };
}

/**
 * Tears down local session state and navigates to a logged-out landing page.
 * Shared by `useSignOut` (user-initiated) and the 401 forced-logout handler
 * in `use-api.ts`. The caller is responsible for any pre-cleanup (BE logout
 * call, session-expired toast, etc.) and for nulling `persistentStorage.auth`
 * before invoking this.
 *
 * On the idToken branch, performs a full-page redirect to Zitadel's
 * end_session endpoint and skips reactive Pinia cleanup to avoid a brief UI
 * flicker before navigation (IPD-2194). PostHog is reset before the redirect
 * so the `user_logout` event dispatches while PostHog state is still intact.
 *
 * On the non-idToken branch, calls `userStore.logout()` to drive reactive
 * cleanup of the post-navigation UI, then performs an in-app navigation — or a
 * reload when the target is the page already open, which a push would not move.
 *
 * Both branches end with `localStorage.clear()` + `sessionStorage.clear()` +
 * `persistentStorage.clear()` so per-user state (tour-seen flags, preferences,
 * last weight, the CDC DPP eligibility ack key, etc.) does not leak into the
 * next session on the same browser tab (IPD-2481).
 */
export async function endLocalSession(args: {
  idToken: string | undefined;
  redirectPath: string;
  /**
   * Why the session ended, when the landing page has something to say about it. Travels as the
   * OIDC `state` on the Zitadel branch and as the same query parameter on the local one, so the
   * page reads it the same way either way — and the registered post-logout URI stays untouched.
   */
  postLogoutState?: string | null;
  api: Api;
  userStore: ReturnType<typeof useUserStore>;
  router: Router;
}): Promise<void> {
  if (args.idToken) {
    analyticsService.resetUserSession();
    localStorage.clear();
    sessionStorage.clear();
    persistentStorage.clear();
    const postLogoutRedirectUri = `${getAppBaseUrl()}${args.redirectPath}`;
    args.api.zitadel.endSession(args.idToken, postLogoutRedirectUri, args.postLogoutState);
  } else {
    args.userStore.logout();
    localStorage.clear();
    sessionStorage.clear();
    persistentStorage.clear();
    const target = args.postLogoutState
      ? `${args.redirectPath}?state=${encodeURIComponent(args.postLogoutState)}`
      : args.redirectPath;
    // Compared without the query, which is ours: the page is the same page either way.
    if (isCurrentPage(args.router, args.redirectPath)) {
      // Pushing to the page already open is a no-op, and everything on it was built from the state
      // just cleared — the sign-up wizard would stay on the step it was on, signed out. Reload so
      // it comes back logged out, the way the idToken branch does via Zitadel.
      window.location.assign(`${getAppBaseUrl()}${target}`);
      if (ROUTER_BASE_URL) {
        // Hash history (PR previews): that URL differs from this one only after the `#`, so the
        // assignment is a same-document navigation and rebuilds nothing on its own.
        window.location.reload();
      }
      return;
    }
    await args.router.push(target);
  }
}

/** Returns a function that signs the user out (BE logout, clears auth, ends Zitadel session) */
export function useSignOut() {
  const api = useApi();
  const userStore = useUserStore();
  const router = useRouter();
  const logger = useLogger();

  return async function signOut(
    redirectPath: string,
    {skipBeLogout = false, postLogoutState = null}: {skipBeLogout?: boolean; postLogoutState?: string | null} = {},
  ): Promise<void> {
    const idToken = persistentStorage.auth?.id_token;

    if (!skipBeLogout) {
      try {
        await api.me.logout();
      } catch (error) {
          logger.warn('BE logout failed, proceeding with local cleanup', error)();
      }
    }
    // eslint-disable-next-line require-atomic-updates
    persistentStorage.auth = null;

    await endLocalSession({idToken, redirectPath, postLogoutState, api, userStore, router});
  };
}
